import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Input, Button, Skeleton, Sparkline } from "@coremonit/design-system";
import { Terminal, Search, Play, Pause, Sparkles, Regex, Filter } from "lucide-react";
import { useLogClusters, useLogs, useLogsAISummary } from "../api/hooks";
import type { LogLevel } from "@coremonit/types";

const LEVEL_COLORS: Record<LogLevel, string> = { debug: C.textMuted, info: C.blue, warn: C.amber, error: C.red, fatal: C.red };

export function LogsPage() {
  const [q, setQ] = useState("");
  const [level, setLevel] = useState<string>("");
  const [regex, setRegex] = useState(false);
  const [tail, setTail] = useState(false);
  const logs = useLogs({ q: !regex ? q || undefined : undefined, regex: regex ? q || undefined : undefined, level: level || undefined });
  const clusters = useLogClusters();
  const ai = useLogsAISummary();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<Terminal size={16} />}
        title="Log Explorer"
        subtitle={`Search · regex · tail · clustering · AI summary`}
        actions={<Button variant={tail ? "danger" : "secondary"} size="sm" icon={tail ? <Pause size={12} /> : <Play size={12} />} onClick={() => setTail((v) => !v)}>{tail ? "Stop tail" : "Tail"}</Button>}
      />

      {ai.data && (
        <GlassCard glow style={{ padding: 14 }} accent={C.borderBright}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Sparkles size={13} color="#fff" /></div>
            <div><div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>AI log summary</div>
              <div style={{ color: C.textSub, fontSize: 12, lineHeight: 1.6 }}>{ai.data.summary}</div>
            </div>
          </div>
        </GlassCard>
      )}

      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <Input value={q} onChange={setQ} prefix={regex ? <Regex size={13} /> : <Search size={13} />} placeholder={regex ? "Regex e.g. ^upstream.*failed" : "Search log messages…"} />
          </div>
          <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "8px 10px", fontSize: 11 }}>
            <option value="" style={{ background: C.surface }}>All levels</option>
            {(["debug", "info", "warn", "error", "fatal"] as const).map(l => <option key={l} value={l} style={{ background: C.surface }}>{l.toUpperCase()}</option>)}
          </select>
          <Button variant={regex ? "primary" : "secondary"} size="sm" icon={<Regex size={12} />} onClick={() => setRegex((v) => !v)}>Regex</Button>
        </div>
      </GlassCard>

      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12 }}>
        <GlassCard style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, color: C.textMuted, fontSize: 11, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Logs ({logs.data?.items.length ?? 0} of {logs.data?.total ?? 0})</span>
            {tail && <Badge color="red">● TAIL</Badge>}
          </div>
          <div style={{ maxHeight: 620, overflow: "auto", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11 }}>
            {logs.isLoading
              ? <div style={{ padding: 14 }}><Skeleton height={80} /></div>
              : logs.data?.items.map((l) => (
                <div key={l.id} style={{ display: "grid", gridTemplateColumns: "150px 60px 130px 1fr", gap: 8, padding: "5px 12px", borderBottom: `1px solid ${C.border}66`, alignItems: "start" }}>
                  <span style={{ color: C.textMuted, fontSize: 10 }}>{new Date(l.ts).toLocaleTimeString()}</span>
                  <span style={{ color: LEVEL_COLORS[l.level], fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{l.level}</span>
                  <span style={{ color: C.blue, fontSize: 10 }}>{l.service}</span>
                  <span style={{ color: C.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {highlight(l.message, q, regex)}
                  </span>
                </div>
              ))}
          </div>
        </GlassCard>
        <GlassCard style={{ padding: 12 }}>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>Error clusters</div>
          {clusters.isLoading ? <Skeleton height={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 620, overflow: "auto" }}>
              {clusters.data?.items.slice(0, 20).map((c, i) => (
                <div key={i} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    <Badge color={c.severity === "high" ? "red" : c.severity === "medium" ? "amber" : "muted"}>{c.severity}</Badge>
                    <span style={{ color: C.text, fontSize: 11, fontWeight: 700 }}>×{c.count.toLocaleString()}</span>
                  </div>
                  <div style={{ color: C.text, fontSize: 11, marginTop: 6, fontFamily: "ui-monospace, monospace", wordBreak: "break-word" }}>{c.pattern}</div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 4 }}>{c.services.join(", ")}</div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function highlight(text: string, q: string, regex: boolean) {
  if (!q) return text;
  try {
    const re = new RegExp(regex ? q : q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const parts = text.split(re);
    const matches = text.match(re) ?? [];
    return parts.reduce<React.ReactNode[]>((acc, part, i) => {
      acc.push(part);
      if (matches[i]) acc.push(<mark key={i} style={{ background: C.blueDim, color: C.blue, padding: "0 2px", borderRadius: 3 }}>{matches[i]}</mark>);
      return acc;
    }, []);
  } catch {
    return text;
  }
}
